import { ArrowRight, MapPin } from 'lucide-react';
import { useState } from 'react';
import { GreekText } from '../components/GreekText';
import { InlineGreek } from '../components/InlineGreek';
import { PlayButton } from '../components/PlayButton';
import { Illustration, hasIllustration } from '../components/Illustration';
import { getItem, staffOf } from '../content';
import type { Lesson } from '../content/types';
import { clipFor, itemVoice, speechRange } from '../lib/voice';
import { finishLesson } from '../state/store';

interface Props {
  lesson: Lesson;
  onDone: () => void;
}

export function DoneMark() {
  return (
    <div className="done-mark" aria-hidden="true">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12.5l4.2 4.2L19 7" />
      </svg>
    </div>
  );
}

/**
 * The end of a lesson: the can-do, honestly "started" (it is solid once the reviews say so), a
 * small real-world mission, and the phrases to hear once more. Progress as what the learner can
 * do, not as points.
 */
export function LessonDone({ lesson, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="step">
      <div className="stack-lg">
        {hasIllustration(lesson.id) ? (
          <div className="postcard-frame">
          <div className="postcard" aria-hidden="true">
            {/* How the scene ended, when there is a picture of it; otherwise how it began. */}
            <Illustration id={hasIllustration(`${lesson.id}-done`) ? `${lesson.id}-done` : lesson.id} size="scene" priority decorative />
            <span className="stamp">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5l4.2 4.2L19 7" />
              </svg>
              <span>done</span>
            </span>
          </div>
          </div>
        ) : (
          <DoneMark />
        )}
        <div className="stack">
          <p className="eyebrow">New can-do · settling in</p>
          <h2 className="title-md">You've started to {lesson.cando.charAt(0).toLowerCase() + lesson.cando.slice(1)}</h2>
          <p className="muted">It's solid once the reviews over the next days go well. They come back just before you'd forget.</p>
        </div>
      </div>
      <div className="panel-quiet row" style={{ alignItems: 'flex-start' }}>
        <MapPin size={22} aria-hidden="true" style={{ color: 'var(--ink-2)', flex: 'none', marginTop: 2 }} />
        <div className="stack" style={{ gap: 2 }}>
          <p className="section-title">Try it for real</p>
          <p>
            <InlineGreek text={lesson.mission} />
          </p>
        </div>
      </div>
      <ul className="met-list">
        {lesson.phrases.map((id) => {
          const item = getItem(id);
          const clip = clipFor(item, itemVoice(item, staffOf(item)));
          return (
            <li key={id} className="met-row">
              <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                <GreekText text={item.text} size="body" />
                <span className="small muted">{item.meaning}</span>
              </div>
              <PlayButton src={clip.src} tag={`done:${id}`} label={`Play «${item.text}»`} {...speechRange(clip)} variant="icon" />
            </li>
          );
        })}
      </ul>
      <div className="actions">
        <button
          type="button"
          className="btn btn-primary btn-block btn-lg"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await finishLesson(lesson.id);
            onDone();
          }}
        >
          Continue <ArrowRight size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
